import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataFlowStore } from '@/stores/dataflowStore';
import { dataflowApi } from '@/services/api/dataflow';
import { TaskTypeBadge } from '@/components/dataflow/TaskTypeBadge';
import type { Instance } from '@/types/dataflow';

const EMPTY_INSTANCE_LOGS: Array<{ ts: string; level: string; message: string }> = [];

function toText(value: unknown): string {
  if (value == null) return '-';
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const STOCK_POOL_LABELS: Record<string, string> = {
  all_a: '全A股',
  single_index: '单一指数',
  multi_index: '多指数合并',
  single_symbol: '单一股票',
  symbol_list: '股票列表',
};

const DATE_RANGE_LABELS: Record<string, string> = {
  today: '今天',
  single: '指定日期',
  range: '日期区间',
  week: '整周',
  month: '整月',
  year: '整年',
};

const INDEX_LABELS: Record<string, string> = {
  hs300: '沪深300',
  zz500: '中证500',
  zz1000: '中证1000',
  zz2000: '中证2000',
};

function renderStockPoolRows(
  stockPoolType: string | undefined,
  stockPoolParams: Record<string, unknown> | undefined
): Array<{ key: string; label: string; value: string }> {
  const rows: Array<{ key: string; label: string; value: string }> = [];
  const type = stockPoolType || 'all_a';
  const params = stockPoolParams || {};

  rows.push({
    key: 'stock_pool_type',
    label: '股票池',
    value: STOCK_POOL_LABELS[type] ? `${STOCK_POOL_LABELS[type]} (${type})` : type,
  });

  switch (type) {
    case 'single_index':
      if (params.index_code) {
        const code = String(params.index_code);
        rows.push({
          key: 'index_code',
          label: '指数',
          value: INDEX_LABELS[code] || code,
        });
      }
      break;
    case 'multi_index':
      if (Array.isArray(params.index_codes) && params.index_codes.length > 0) {
        const codes = params.index_codes.map((c) => INDEX_LABELS[String(c)] || String(c));
        rows.push({
          key: 'index_codes',
          label: '指数列表',
          value: codes.join(', '),
        });
      }
      break;
    case 'single_symbol':
      if (params.symbol) {
        rows.push({
          key: 'symbol',
          label: '股票代码',
          value: String(params.symbol),
        });
      }
      break;
    case 'symbol_list':
      if (Array.isArray(params.symbols) && params.symbols.length > 0) {
        const symbols = params.symbols.slice(0, 5).join(', ');
        const more = params.symbols.length > 5 ? ` (+${params.symbols.length - 5} more)` : '';
        rows.push({
          key: 'symbols',
          label: '股票列表',
          value: symbols + more,
        });
      }
      break;
  }

  return rows;
}

function resolveStockPoolConfigFromInstance(
  instance: Instance | undefined
): { stockPoolType: string | undefined; stockPoolParams: Record<string, unknown> | undefined } {
  if (!instance) {
    return { stockPoolType: undefined, stockPoolParams: undefined };
  }

  if (instance.stock_pool_type) {
    return {
      stockPoolType: instance.stock_pool_type,
      stockPoolParams: instance.stock_pool_params || {},
    };
  }

  const scopeType = String(instance.scope_type || "").trim().toLowerCase();
  const rawScopeJson = instance.scope_json;
  const scopeJson =
    rawScopeJson && typeof rawScopeJson === "object" && !Array.isArray(rawScopeJson)
      ? (rawScopeJson as Record<string, unknown>)
      : {};
  const symbolGroupType = String(scopeJson.symbol_group_type || "").trim().toLowerCase();

  if (symbolGroupType === "single_symbol") {
    const symbol = String(scopeJson.single_symbol || scopeJson.symbol || "").trim();
    return {
      stockPoolType: "single_symbol",
      stockPoolParams: symbol ? { symbol } : {},
    };
  }
  if (symbolGroupType === "symbol_list") {
    const symbols = Array.isArray(scopeJson.symbols)
      ? scopeJson.symbols.map((item) => String(item).trim()).filter(Boolean)
      : [];
    return {
      stockPoolType: "symbol_list",
      stockPoolParams: { symbols },
    };
  }

  if (scopeType === "symbols") {
    const symbolsRaw = scopeJson.symbols;
    const symbols = Array.isArray(symbolsRaw)
      ? symbolsRaw.map((item) => String(item).trim()).filter(Boolean)
      : typeof symbolsRaw === "string"
      ? symbolsRaw.split(",").map((item) => item.trim()).filter(Boolean)
      : [];
    if (symbols.length === 1) {
      return { stockPoolType: "single_symbol", stockPoolParams: { symbol: symbols[0] } };
    }
    if (symbols.length > 1) {
      return { stockPoolType: "symbol_list", stockPoolParams: { symbols } };
    }
  }

  if (scopeType === "universe") {
    const indexCode = String(scopeJson.index_code || scopeJson.universe || "").trim().toLowerCase();
    if (indexCode && indexCode !== "all_a") {
      return { stockPoolType: "single_index", stockPoolParams: { index_code: indexCode } };
    }
  }

  return { stockPoolType: "all_a", stockPoolParams: {} };
}

function resolveDateRangeConfigFromInstance(
  instance: Instance | undefined
): { dateRangeType: string | undefined; dateRangeParams: Record<string, unknown> | undefined } {
  if (!instance) {
    return { dateRangeType: undefined, dateRangeParams: undefined };
  }
  const type = String(instance.date_range_type || "today");
  const params =
    instance.date_range_params && typeof instance.date_range_params === "object" && !Array.isArray(instance.date_range_params)
      ? ({ ...instance.date_range_params } as Record<string, unknown>)
      : {};
  const scopeJson =
    instance.scope_json && typeof instance.scope_json === "object" && !Array.isArray(instance.scope_json)
      ? (instance.scope_json as Record<string, unknown>)
      : {};
  const createdDate = String(instance.created_at || "").split(" ", 1)[0] || "";
  const scopeTradeDate = String(scopeJson.trade_date || scopeJson.date || "").trim();

  if ((type === "today" || type === "single") && !params.date) {
    params.date = scopeTradeDate || createdDate;
  }
  if (type === "range") {
    if (!params.start_date && scopeJson.start_date) params.start_date = String(scopeJson.start_date);
    if (!params.end_date && scopeJson.end_date) params.end_date = String(scopeJson.end_date);
  }
  if (type === "week" && !params.week_value && scopeJson.week_value) {
    params.week_value = String(scopeJson.week_value);
  }
  if (type === "month" && !params.month_value && scopeJson.month_value) {
    params.month_value = String(scopeJson.month_value);
  }
  if (type === "year" && !params.year_value && scopeJson.year_value) {
    params.year_value = String(scopeJson.year_value);
  }
  return { dateRangeType: type, dateRangeParams: params };
}

function renderDateRangeRows(
  dateRangeType: string | undefined,
  dateRangeParams: Record<string, unknown> | undefined
): Array<{ key: string; label: string; value: string }> {
  const rows: Array<{ key: string; label: string; value: string }> = [];
  const type = dateRangeType || 'today';
  const params = dateRangeParams || {};

  rows.push({
    key: 'date_range_type',
    label: '日期范围',
    value: DATE_RANGE_LABELS[type] || type,
  });

  switch (type) {
    case 'single':
      if (params.date) {
        rows.push({
          key: 'date',
          label: '日期',
          value: String(params.date),
        });
      }
      break;
    case 'today':
      if (params.date) {
        rows.push({
          key: 'today_date',
          label: '日期',
          value: String(params.date),
        });
      }
      break;
    case 'range':
      if (params.start_date || params.end_date) {
        rows.push({
          key: 'date_range',
          label: '区间',
          value: `${String(params.start_date || '')} ~ ${String(params.end_date || '')}`,
        });
      }
      break;
    case 'week':
      if (params.week_value) {
        rows.push({
          key: 'week',
          label: '周',
          value: String(params.week_value),
        });
      }
      break;
    case 'month':
      if (params.month_value) {
        rows.push({
          key: 'month',
          label: '月份',
          value: String(params.month_value),
        });
      }
      break;
    case 'year':
      if (params.year_value) {
        rows.push({
          key: 'year',
          label: '年份',
          value: String(params.year_value),
        });
      }
      break;
  }

  return rows;
}

function renderOptionsRows(options: Record<string, unknown> | undefined): Array<{ key: string; label: string; value: string }> {
  const data = options || {};
  return Object.keys(data)
    .sort()
    .map((key) => ({
      key,
      label: `Option.${key}`,
      value: toText(data[key]),
    }));
}

function renderSummaryRows(
  summary: Record<string, unknown> | undefined,
  fallbackError: string | null | undefined
): Array<{ key: string; label: string; value: string }> {
  const rows: Array<{ key: string; label: string; value: string }> = [];
  const payload =
    summary && typeof summary === "object" && !Array.isArray(summary)
      ? summary
      : {};

  const title = payload.title ? String(payload.title) : "";
  const message = payload.message ? String(payload.message) : "";
  if (title) {
    rows.push({ key: "summary_title", label: "标题", value: title });
  }
  if (message) {
    rows.push({ key: "summary_message", label: "摘要", value: message });
  }

  const metricsRaw = payload.metrics;
  if (metricsRaw && typeof metricsRaw === "object" && !Array.isArray(metricsRaw)) {
    const metrics = metricsRaw as Record<string, unknown>;
    Object.keys(metrics)
      .sort()
      .forEach((key) => {
        rows.push({
          key: `summary_metric_${key}`,
          label: `指标.${key}`,
          value: toText(metrics[key]),
        });
      });
  }

  if (rows.length === 0 && fallbackError) {
    rows.push({
      key: "summary_error_fallback",
      label: "摘要",
      value: String(fallbackError),
    });
  }
  return rows;
}

export function InstanceDetail() {
  const queryClient = useQueryClient();
  const selectedInstanceId = useDataFlowStore((s) => s.selectedInstanceId);
  const setSelectedInstance = useDataFlowStore((s) => s.setSelectedInstance);
  const clearInstanceLogs = useDataFlowStore((s) => s.clearInstanceLogs);
  const instanceLogs = useDataFlowStore((s) =>
    selectedInstanceId ? s.instanceLogs[selectedInstanceId] || EMPTY_INSTANCE_LOGS : EMPTY_INSTANCE_LOGS
  );

  const { data: runtimeInstances = [] } = useQuery({
    queryKey: ['dataflow', 'instances'],
    queryFn: () => dataflowApi.getInstances(),
    enabled: selectedInstanceId != null,
  });

  const { data: instance, error: instanceError, isLoading: instanceLoading } = useQuery({
    queryKey: ['dataflow', 'instances', selectedInstanceId],
    queryFn: () => dataflowApi.getInstance(selectedInstanceId!),
    enabled: selectedInstanceId != null,
    refetchInterval: 3000,
  });

  const { data: historicalLogs } = useQuery({
    queryKey: ['dataflow', 'instances', selectedInstanceId, 'logs'],
    queryFn: () => dataflowApi.getInstanceLogs(selectedInstanceId!, 200),
    enabled: selectedInstanceId != null,
  });

  const startMutation = useMutation({
    mutationFn: () => dataflowApi.retryInstance(selectedInstanceId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataflow', 'instances'] });
      queryClient.invalidateQueries({ queryKey: ['dataflow', 'batches'] });
      queryClient.invalidateQueries({ queryKey: ['dataflow', 'instances', selectedInstanceId] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => dataflowApi.cancelInstance(selectedInstanceId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataflow', 'instances'] });
      queryClient.invalidateQueries({ queryKey: ['dataflow', 'batches'] });
      queryClient.invalidateQueries({ queryKey: ['dataflow', 'instances', selectedInstanceId] });
    },
  });

  // Clear realtime logs when switching away from this instance to avoid mixing
  useEffect(() => {
    if (selectedInstanceId) {
      return () => {
        clearInstanceLogs(selectedInstanceId);
      };
    }
  }, [selectedInstanceId, clearInstanceLogs]);

  if (!selectedInstanceId) return null;

  const instanceFromList = runtimeInstances.find((item: Instance) => item.id === selectedInstanceId);
  const resolvedInstance = instance ?? instanceFromList;

  // Merge historical + real-time logs, dedup by id for historical, append realtime.
  // Keep hooks below unconditional so React hook ordering is stable across loading states.
  const displayLogs = [
    ...(historicalLogs || []),
    ...instanceLogs.map((l, idx) => ({
      id: `rt-${idx}`,
      ts: l.ts,
      level: l.level,
      message: l.message,
    })),
  ];

  // Smart scroll handling for logs
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const userScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isNearBottomRef = useRef(true);

  // Check if scroll is near bottom
  const checkIsNearBottom = () => {
    const container = logsContainerRef.current;
    if (!container) return true;
    const threshold = 50; // pixels from bottom
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom < threshold;
  };

  // Handle scroll event - detect user interaction
  const handleScroll = () => {
    const wasNearBottom = isNearBottomRef.current;
    isNearBottomRef.current = checkIsNearBottom();

    // If user scrolls up (away from bottom), mark as user scrolling
    if (wasNearBottom && !isNearBottomRef.current) {
      setIsUserScrolling(true);
      // Clear existing timeout
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
      // Resume auto-scroll after 3 seconds
      userScrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      }, 3000);
    }
  };

  // Auto-scroll to bottom when new logs arrive (if not user scrolling)
  useEffect(() => {
    if (isUserScrolling) return;
    const container = logsContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [displayLogs.length, isUserScrolling]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, []);

  if (!resolvedInstance && instanceLoading) {
    return (
      <div className="h-full flex items-center justify-center text-[#64748b] text-sm">
        Loading instance...
      </div>
    );
  }

  const status = resolvedInstance?.status || 'RUNNING';
  const statusColor =
    status === 'COMPLETED'
      ? 'text-[#22c55e]'
      : status === 'FAILED'
      ? 'text-[#dc2626]'
      : status === 'QUEUED'
      ? 'text-[#64748b]'
      : 'text-[#eab308]';

  const statusLabel =
    status === 'RUNNING'
      ? 'RUNNING'
      : status === 'PENDING'
      ? 'PENDING'
      : status === 'COMPLETED'
      ? 'COMPLETED'
      : status === 'FAILED'
      ? 'FAILED'
      : status === 'QUEUED'
      ? 'QUEUED'
      : 'CANCELLED';

  const progress = resolvedInstance?.progress_pct ?? 0;
  const processed = resolvedInstance?.progress_current ?? 0;
  const total = resolvedInstance?.progress_total ?? 0;
  const resolvedStockPoolConfig = resolveStockPoolConfigFromInstance(resolvedInstance);
  const resolvedDateRangeConfig = resolveDateRangeConfigFromInstance(resolvedInstance);
  const stockPoolRows = renderStockPoolRows(
    resolvedStockPoolConfig.stockPoolType,
    resolvedStockPoolConfig.stockPoolParams
  );
  const dateRangeRows = renderDateRangeRows(
    resolvedDateRangeConfig.dateRangeType,
    resolvedDateRangeConfig.dateRangeParams
  );
  const optionRows = renderOptionsRows(resolvedInstance?.options_json);
  const summaryRows = renderSummaryRows(resolvedInstance?.summary_json, resolvedInstance?.error_msg);
  const canStart = status === 'PENDING' || status === 'QUEUED' || status === 'FAILED' || status === 'CANCELLED';
  const startLabel = status === 'FAILED' || status === 'CANCELLED' ? 'Retry' : 'Start';
  const canCancel = status === 'PENDING' || status === 'QUEUED' || status === 'RUNNING';
  const cancelLabel = status === 'RUNNING' ? 'Cancel (Request)' : 'Cancel';

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b border-[var(--border)] bg-[#1a1d24]">
        <div className="flex items-center justify-between">
          <div>
            <div className={`text-xs mb-1 ${statusColor}`}>● {statusLabel}</div>
            <div className="text-[#f8fafc] text-sm font-semibold flex items-center gap-2">
              <TaskTypeBadge taskType={resolvedInstance?.task_type || 'task'} />
              {" "}
              <span className="text-[#64748b]">#{selectedInstanceId}</span>
            </div>
          </div>
          <button
            onClick={() => setSelectedInstance(null)}
            className="text-[#64748b] hover:text-[#e2e8f0]"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="flex-1 p-3 overflow-auto space-y-4">
        {instanceError && instanceFromList && (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">
            Detail refresh failed{instanceError instanceof Error && instanceError.message ? `: ${instanceError.message}` : ''}. Using runtime list data.
          </div>
        )}

        {/* Progress */}
        <div className="bg-[#0f1115] border border-[var(--border)] rounded p-3">
          <div className="text-[#64748b] text-[10px] uppercase mb-2">Progress</div>
          <div className="bg-[#1e293b] h-2 rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-[#eab308] rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-[#94a3b8]">
            <span>
              {total > 0 ? `${processed} / ${total}` : ''}
            </span>
            <span>{progress.toFixed(0)}%</span>
          </div>
        </div>

        {/* Current Step */}
        {resolvedInstance?.current_step && (
          <div>
            <div className="text-[#64748b] text-[10px] uppercase mb-2">Current Step</div>
            <div className="text-[#e2e8f0] text-sm">{resolvedInstance.current_step}</div>
          </div>
        )}

        <div className="bg-[#0f1115] border border-[var(--border)] rounded p-3">
          <div className="text-[#64748b] text-[10px] uppercase mb-2">Runtime Params</div>
          <div className="grid grid-cols-1 gap-2 text-xs">
            <div className="flex items-start justify-between gap-3">
              <span className="text-[#64748b]">触发方式</span>
              <span className="text-[#e2e8f0] font-mono text-right break-all">
                {resolvedInstance?.trigger_mode || 'manual'}
              </span>
            </div>
            {stockPoolRows.map((row) => (
              <div key={row.key} className="flex items-start justify-between gap-3">
                <span className="text-[#64748b]">{row.label}</span>
                <span className="text-[#e2e8f0] font-mono text-right break-all">{row.value}</span>
              </div>
            ))}
            {dateRangeRows.length > 0 && (
              <div className="pt-1 text-[10px] uppercase text-[#64748b] border-t border-[var(--border)]">
                日期范围
              </div>
            )}
            {dateRangeRows.map((row) => (
              <div key={row.key} className="flex items-start justify-between gap-3">
                <span className="text-[#64748b]">{row.label}</span>
                <span className="text-[#e2e8f0] font-mono text-right break-all">{row.value}</span>
              </div>
            ))}
            {optionRows.length > 0 && (
              <div className="pt-1 text-[10px] uppercase text-[#64748b] border-t border-[var(--border)]">
                选项
              </div>
            )}
            {optionRows.map((row) => (
              <div key={row.key} className="flex items-start justify-between gap-3">
                <span className="text-[#64748b]">{row.label}</span>
                <span className="text-[#e2e8f0] font-mono text-right break-all">{row.value}</span>
              </div>
            ))}
            {summaryRows.length > 0 && (
              <div className="pt-1 text-[10px] uppercase text-[#64748b] border-t border-[var(--border)]">
                任务总结
              </div>
            )}
            {summaryRows.map((row) => (
              <div key={row.key} className="flex items-start justify-between gap-3">
                <span className="text-[#64748b]">{row.label}</span>
                <span className="text-[#e2e8f0] font-mono text-right break-all">{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          {canStart && (
            <button
              onClick={() => startMutation.mutate()}
              disabled={startMutation.isPending}
              className="flex-1 py-2 bg-[#16a34a] text-white text-xs rounded hover:bg-[#15803d] disabled:opacity-50"
            >
              {startMutation.isPending ? 'Starting...' : startLabel}
            </button>
          )}
          {canCancel && (
            <button
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              className="flex-1 py-2 bg-[#dc2626] text-white text-xs rounded hover:bg-[#b91c1c] disabled:opacity-50"
            >
              {cancelMutation.isPending ? 'Cancelling...' : cancelLabel}
            </button>
          )}
          {startMutation.error && (
            <div className="text-[11px] text-rose-300">
              Start failed: {startMutation.error instanceof Error ? startMutation.error.message : 'unknown'}
            </div>
          )}
          {cancelMutation.error && (
            <div className="text-[11px] text-rose-300">
              Cancel failed: {cancelMutation.error instanceof Error ? cancelMutation.error.message : 'unknown'}
            </div>
          )}
        </div>

        {/* Logs */}
        <div className="border border-[var(--border)] rounded bg-[#0f1115]">
          <div className="text-[#64748b] text-[10px] uppercase p-2 border-b border-[var(--border)] flex items-center justify-between">
            <span>Logs</span>
            {isUserScrolling && (
              <span className="text-[10px] text-amber-400">Auto-scroll paused</span>
            )}
          </div>
          <div
            ref={logsContainerRef}
            onScroll={handleScroll}
            className="p-2 h-64 overflow-auto font-mono text-[10px] space-y-1"
          >
            {displayLogs.length === 0 && (
              <div className="text-[#64748b]">No logs available</div>
            )}
            {displayLogs.map((log) => (
              <div
                key={log.id}
                className={`break-words ${
                  log.level === 'ERROR'
                    ? 'text-[#fca5a5]'
                    : log.level === 'WARN'
                    ? 'text-[#fde047]'
                    : 'text-[#94a3b8]'
                }`}
              >
                <span className="text-[#475569]">[{log.ts}]</span>{' '}
                <span className="font-semibold">{log.level}</span>{' '}
                {log.message}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
